// @ts-strict-ignore
import * as React from 'react';
import { useMemo } from 'react';
import {
    AllPatternTokens,
    AppNotification,
    CommsChannelAvailableTokens,
    AppNotificationType,
    AppPlatform,
    BundleSizeAlertNotifyFor,
    BundleSizeConditionUnit,
    BundleSizeConditionType,
    BundleSizeIncreaseCondition,
    CommonNotification,
    IntegrationCapability,
    IntegrationMicrosoftTeams,
    IntegrationProvider,
    IntegrationProviderType,
    IntegrationSlack,
    MonitoringAutomationConditionType,
    NotificationDestination,
    OrgUser,
    PatternToken,
    ProfileOrCertificateType,
    SettingType,
    UserGroup,
    UserGroupId,
} from '../../types';
import Text from 'antd/lib/typography/Text';
import AntLink from 'antd/lib/typography/Link';
import Space from 'antd/lib/space';
import { useSelector } from 'react-redux';
import { commsChannelsValidatorFunction } from '../../helpers';
import {
    automationAndNotificationSettingsModalContentUsingFormInstance,
    emailValidationRules,
    FieldType,
    filterOptionLabel,
    filterOptionPrefixMatch,
    FormFieldData,
} from '../../helpers/formHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppIntegrations,
    selectAppPlatform,
    selectConnectedCapabilitiesForApp,
    selectJoinedCommsProvidersNamesList,
    selectOrgGroups,
    selectOrgProviders,
} from '../../selectors/selectors';
import { AppComponentProps, AppRouteComponentMatchParams, MonitoringConditionOption } from '../../typescriptHelpers';
import Select from 'antd/lib/select';
import { Link, useParams } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import Alert from 'antd/lib/alert';
import { globalMessage } from '../../utils/globalMessage';
import Radio from 'antd/lib/radio';
import {
    displayTextForAutomationConditionType,
    displayTextForUserChecklistGroup,
    displayUserNameLong,
} from '../../helpers/displayHelpers';
import { getIsApplePlatform } from '../../helpers';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import TimePicker from 'antd/lib/time-picker';
import { dayjs } from '@/extendedDayjs';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import InputNumber from 'antd/lib/input-number';
import IntegrationIcon from '../icons/IntegrationIcon';
import Button from 'antd/lib/button';
import * as API from '@/api';
import { SendNotificationTestDMRequest } from '@/actions/app';
import Tooltip from 'antd/lib/tooltip';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import LinkExternalIcon from '../icons/LinkExternal';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import { Checkbox, Flex } from 'antd/lib';
import TextArea from 'antd/lib/input/TextArea';
import Popover from 'antd/lib/popover';
import { PatternTokenHelperTextComponent } from '@/components/appSettings/PatternTokenHelperTextComponent';
import { generateThresholdConditionsField } from '../automations/AutomationSettings';

export enum MentionType {
    MentionGroups = 'mentionGroups',
    MentionUsers = 'mentionUsers',
}

export interface CommonNotificationSettingsModalProps extends AppComponentProps {
    open: boolean;
    notificationId: string;
    onClose: () => void;
    onAfterClose: () => void;
    actionType: string;

    onSubmit: (formValues: any) => void;
    notification: CommonNotification;
    actionButtonText?: string;
}

const NotificationSectionWrapper: React.FC<{
    title: string;
    children: React.ReactNode;
    headerAction?: React.ReactNode;
}> = ({ title, children, headerAction }) => (
    <div
        style={{
            backgroundColor: antGrey.grey2,
            borderRadius: 8,
            padding: 16,
            border: `1px solid ${antGrey.grey5}`,
            marginBottom: 16,
        }}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text style={{ fontWeight: 600, color: antGrey.grey8, fontSize: 14 }}>{title}</Text>
            {headerAction}
        </div>
        {children}
    </div>
);

const NotificationChannelSection: React.FC<{
    title: string;
    children: React.ReactNode;
    headerAction?: React.ReactNode;
    isChecked: boolean;
    onCheckboxChange: (checked: boolean) => void;
    disabled?: boolean;
}> = ({ title, children, headerAction, isChecked, onCheckboxChange, disabled }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <Checkbox
            checked={isChecked}
            onChange={(e) => onCheckboxChange(e.target.checked)}
            disabled={disabled}
            style={{ marginTop: 4 }}
        />
        <div style={{ flex: 1 }}>
            <NotificationSectionWrapper title={title} headerAction={headerAction}>
                {children}
            </NotificationSectionWrapper>
        </div>
    </div>
);

const FormErrorText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text style={{ color: runwayColors.statusRed, fontSize: 14 }}>{children}</Text>
);

const createErrorField = (errorMessage: string): FormFieldData => ({
    fieldType: FieldType.Custom,
    renderField: () => <FormErrorText>{errorMessage}</FormErrorText>,
    extraFormItemProps: { style: { marginTop: -8, marginBottom: 16 } },
});

const directDownloadAccessOrExpiryRules = [
    ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
        validator(_: unknown, value: unknown) {
            if (!getFieldValue('includeDirectDownloadLink')) {
                return Promise.resolve();
            }
            if (value === null || value === undefined || value === '') {
                return Promise.resolve();
            }
            const n = Number(value);
            if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
                return Promise.reject(new Error('Must be a whole number greater than 0'));
            }
            return Promise.resolve();
        },
    }),
];

/** Sibling of the includeDirectDownloadLink field — must not be nested under that Form.Item or Ant Design treats
 * access/expiry as nested values and overwrites the checkbox boolean when InputNumber updates. */
const DirectDownloadLinkLimitFields: React.FC<{
    notification: AppNotification;
    isDisabled: boolean;
}> = ({ notification, isDisabled }) => {
    const watched = Form.useWatch('includeDirectDownloadLink');
    // useWatch can be undefined before the field is registered; coalesce so we don't treat that as "off" and
    // unmount the limit fields (mount/unmount + layout shift reads as flashing, especially while scrolling).
    const includeOn = watched === undefined ? (notification.includeDirectDownloadLink ?? false) : Boolean(watched);

    return (
        <div
            style={{
                display: includeOn ? 'block' : 'none',
                width: '100%',
                paddingLeft: 24,
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Form.Item
                    name="directDownloadLinkAccessLimit"
                    label="Max downloads"
                    initialValue={notification.directDownloadLinkAccessLimit ?? null}
                    style={{ marginBottom: 0 }}
                    dependencies={['includeDirectDownloadLink']}
                    rules={directDownloadAccessOrExpiryRules}
                >
                    <InputNumber
                        min={1}
                        precision={0}
                        placeholder="Unlimited"
                        style={{ width: 140 }}
                        disabled={isDisabled}
                    />
                </Form.Item>
                <Form.Item
                    name="directDownloadLinkExpiryHours"
                    label="Expiry (hours)"
                    initialValue={notification.directDownloadLinkExpiryHours ?? null}
                    style={{ marginBottom: 0 }}
                    dependencies={['includeDirectDownloadLink']}
                    rules={directDownloadAccessOrExpiryRules}
                >
                    <InputNumber
                        min={1}
                        precision={0}
                        placeholder="No expiry"
                        style={{ width: 140 }}
                        disabled={isDisabled}
                    />
                </Form.Item>
            </Space>
        </div>
    );
};

const createDestinationDependentField = (
    base: Omit<FormFieldData, 'renderField'>,
    renderContent: (isSlackEnabled: boolean) => React.ReactNode,
    isDisabled: boolean,
    getDestinationStates: (form?: any) => { slack: boolean; email: boolean },
): FormFieldData => ({
    ...base,
    extraFormItemProps: { ...base.extraFormItemProps, dependencies: ['notificationChannels'] },
    renderField: (data, value, onChange, form) => {
        const { slack: isSlackEnabled } = getDestinationStates(form);
        return renderContent(isSlackEnabled);
    },
});

interface NotificationSettingsModalFooterProps {
    form: ReturnType<typeof Form.useForm>[0];
    onClose: () => void;
    notificationId: string;
    appPlatform?: AppPlatform;
    loading: boolean;
    actionButtonText?: string;
}

const NotificationSettingsModalFooter: React.FC<NotificationSettingsModalFooterProps> = ({
    form,
    onClose,
    notificationId,
    appPlatform,
    loading,
    actionButtonText,
}) => {
    const enabledValue = Form.useWatch('enabled', form);
    const bundleSizeConditions = Form.useWatch('bundleSizeIncreaseConditions', form);
    const bundleSizeCellular = Form.useWatch('bundleSizeCellularDownloadLimitThresholdMB', form);
    const isBundleSizeNotification = notificationId === AppNotificationType.BundleSizeIncrease;
    const isApple = appPlatform === undefined || getIsApplePlatform(appPlatform);
    const hasBundleSizeConditions = Array.isArray(bundleSizeConditions) && bundleSizeConditions.length > 0;
    const hasBundleSizeCellular =
        isApple && bundleSizeCellular != null && bundleSizeCellular !== '' && Number(bundleSizeCellular) >= 1;
    const saveDisabledDueToBundleSize =
        isBundleSizeNotification && enabledValue === true && !hasBundleSizeConditions && !hasBundleSizeCellular;

    return (
        <Space>
            <Button key="back" onClick={onClose}>
                Cancel
            </Button>
            <Button
                key="submit"
                type="primary"
                loading={loading}
                disabled={saveDisabledDueToBundleSize}
                onClick={() => form.submit()}
            >
                {actionButtonText ?? 'Save'}
            </Button>
        </Space>
    );
};

const CommonNotificationSettingsModal: React.FC<CommonNotificationSettingsModalProps> = (
    props: CommonNotificationSettingsModalProps,
) => {
    const [form] = Form.useForm();

    const mentionType = Form.useWatch('mentionType', form);
    const selectedNotificationChannels = Form.useWatch('notificationChannels', form);
    const enabledValue = Form.useWatch('enabled', form);
    const isDisabled = enabledValue === false;
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const notificationsProvider = useSelector((state: RootState) => {
        const integrations = selectAppIntegrations(state, props.appId);
        const notificationsIntegration = integrations.find((integration) =>
            integration.capabilities.includes(IntegrationCapability.Notifications),
        );

        if (notificationsIntegration) {
            const providers = selectOrgProviders(state, props.orgId);
            const notificationsProvider = providers.find(
                (provider) => provider.id === notificationsIntegration.providerId,
            );

            return notificationsProvider;
        }

        return null;
    });

    const notification = props.notification;

    const [slackChecked, setSlackChecked] = React.useState(
        notification.notificationChannels?.includes(NotificationDestination.Slack) ?? true,
    );
    const [emailChecked, setEmailChecked] = React.useState(
        notification.notificationChannels?.includes(NotificationDestination.Email) || false,
    );

    const notificationsIntegration = useSelector((state: RootState) => {
        const integrations = selectAppIntegrations(state, props.appId);
        const notificationsIntegration = integrations.find((integration) =>
            integration.capabilities.includes(IntegrationCapability.Notifications),
        );

        return (
            (notificationsIntegration as IntegrationSlack) || (notificationsIntegration as IntegrationMicrosoftTeams)
        );
    });

    const hasNotificationsIntegration = !!notificationsIntegration;
    const primaryChannels =
        hasNotificationsIntegration ?
            notificationsIntegration.data?.primaryChannels?.map((chan) => chan.name).join(', ')
        :   null;

    const updateNotificationSettingIsLoading = useIsLoading(props.actionType, props.notificationId);
    const updateNotificationSettingSuccess = useUpdated(props.actionType, props.notificationId);
    const updateNotificationSettingError = useError(props.actionType, props.notificationId);

    const onSubmit = async (form) => {
        try {
            const formValues = await form.validateFields();

            if (formValues.thresholdConditions) {
                for (const condition of formValues.thresholdConditions) {
                    switch (condition.id) {
                        case MonitoringAutomationConditionType.Adoption:
                            formValues.adoptionThreshold = condition.threshold;
                            break;
                        case MonitoringAutomationConditionType.PhasedReleaseDay:
                            formValues.phasedReleaseDayThreshold = condition.threshold;
                            break;
                        default:
                            break;
                    }
                }

                delete formValues.thresholdConditions;
            }

            return props.onSubmit(formValues);
        } catch (error) {
            globalMessage.error(error.message);
        }
    };

    const [isSendTestDMLoading, setIsSendTestDMLoading] = React.useState(false);
    const [showMoreInfo, setShowMoreInfo] = React.useState(false);

    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, props.appId));
    const groups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const connectedCapabilities = useSelector((state: RootState) =>
        selectConnectedCapabilitiesForApp(state, props.appId),
    );

    const getNotificationDestinationsState = useMemo(() => {
        return (form?: any) => {
            if (isDisabled) {
                return { slack: false, email: false };
            }
            const currentChannels =
                selectedNotificationChannels ||
                form?.getFieldValue('notificationChannels') ||
                notification.notificationChannels;
            const slack =
                !notification.allowEmails ||
                (currentChannels && currentChannels.includes(NotificationDestination.Slack));
            const email = !!(currentChannels && currentChannels.includes(NotificationDestination.Email));
            return { slack, email };
        };
    }, [selectedNotificationChannels, notification, isDisabled]);

    const handleChannelCheckboxChange = (channel: NotificationDestination, checked: boolean) => {
        if (channel === NotificationDestination.Slack) {
            setSlackChecked(checked);
        } else {
            setEmailChecked(checked);
        }

        const currentChannels = form.getFieldValue('notificationChannels') || [];
        const updatedChannels =
            checked ?
                [...currentChannels.filter((c) => c !== channel), channel]
            :   currentChannels.filter((c) => c !== channel);
        form.setFieldsValue({ notificationChannels: updatedChannels });
    };

    React.useEffect(() => {
        if (updateNotificationSettingSuccess) {
            globalMessage.success('Updated notification settings!');
            props.onClose();
        }
    }, [updateNotificationSettingSuccess]);

    React.useEffect(() => {
        if (updateNotificationSettingError) {
            globalMessage.error(updateNotificationSettingError);
        }
    }, [updateNotificationSettingError]);

    React.useEffect(() => {
        if (mentionType) {
            const otherField =
                mentionType === MentionType.MentionGroups ? MentionType.MentionUsers : MentionType.MentionGroups;

            form.setFieldsValue({ [otherField]: [] });
        }
    }, [mentionType, form]);

    const auth = useSelector((state: RootState) => state.auth);
    const authCredentials = auth.authCredentials;
    const sendTestDM = () => {
        const requestData: SendNotificationTestDMRequest = {
            orgId: props.orgId,
            appId: props.appId,
            notificationId: notification.id,
        };

        setIsSendTestDMLoading(true);

        API.sendNotificationTestDM(requestData, authCredentials)
            .then(() => {
                globalMessage.success('Test DM sent!');
                return;
            })
            .catch((e) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => setIsSendTestDMLoading(false));
    };

    const slackHeaderAction =
        notificationsIntegration ?
            <Space size={6}>
                <AntLink
                    onClick={sendTestDM}
                    disabled={!notificationsIntegration}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        opacity: !notificationsIntegration ? 0.5 : 1,
                    }}
                >
                    {notificationsIntegration && <IntegrationIcon integrationId={notificationsIntegration.id} />}
                    {isSendTestDMLoading ? 'Sending...' : 'Send test DM'}
                </AntLink>
                <Tooltip title="A sample test notification will be sent to you via DM">
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
            </Space>
        :   null;

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    const formFields = notificationSettingFormFieldsForNotification({
        notification,
        provider: notificationsProvider,
        hasNotificationsIntegration,
        userGroups: groups,
        mentionType,
        appUsers,
        primaryChannels,
        selectedNotificationChannels,
        isDisabled,
        showMoreInfo,
        setShowMoreInfo,
        getNotificationDestinationsState,
        connectedCapabilities,
        appPlatform,
        commsProviders,
        checkboxActions: {
            slackChecked,
            emailChecked,
            handleChannelCheckboxChange,
            slackHeaderAction,
        },
    });
    const formContent = automationAndNotificationSettingsModalContentUsingFormInstance(
        form,
        formFields,
        false,
        onSubmit,
    );
    const notificationsIntegrationComponentRoute = createAppSettingsRouteWithSettingType(
        orgIdParam,
        appIdParam,
        SettingType.Integrations,
    );

    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <Modal
            open={props.open}
            destroyOnClose
            title={notification.displayText}
            onCancel={props.onClose}
            onOk={() => form.submit()}
            afterClose={() => {
                form.resetFields();
                props.onAfterClose();
            }}
            width={600}
            focusTriggerAfterClose={false}
            footer={
                <NotificationSettingsModalFooter
                    form={form}
                    onClose={props.onClose}
                    notificationId={notification.id}
                    appPlatform={appPlatform}
                    loading={updateNotificationSettingIsLoading}
                    actionButtonText={props.actionButtonText}
                />
            }
        >
            <Space direction="vertical" size={10} wrap>
                {!hasNotificationsIntegration && (
                    <Alert
                        type="warning"
                        message="Notification integration not installed. Notifications cannot be sent until an integration
                    is configured."
                        action={<Link to={notificationsIntegrationComponentRoute}>Go to integrations</Link>}
                    />
                )}
                {!!notification.info && (
                    <Space direction="vertical">
                        <Text type="secondary">Runway will send your team a notification when:</Text>
                        <Text>
                            {notification.info}{' '}
                            <AntLink
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                href={
                                    notification.id === AppNotificationType.BundleSizeIncrease ?
                                        'https://docs.runway.team/notifications/types-of-notifications#build-bundle-sizes'
                                    :   'https://docs.runway.team/notifications/types-of-notifications'
                                }
                                target="_blank/"
                            >
                                More info
                                <LinkExternalIcon
                                    width={16}
                                    height={16}
                                    style={{
                                        opacity: isHovered ? 0.3 : 1,
                                        transition: 'opacity 0.2s ease-in-out',
                                        marginLeft: 4,
                                    }}
                                />
                            </AntLink>
                        </Text>
                    </Space>
                )}
                <div>{formContent}</div>
            </Space>
        </Modal>
    );
};

export default CommonNotificationSettingsModal;

interface NotificationFormFieldsProps {
    notification: AppNotification;
    provider: IntegrationProvider;
    hasNotificationsIntegration: boolean;
    userGroups: Record<UserGroupId, UserGroup>;
    mentionType?: MentionType;
    appUsers: OrgUser[];
    primaryChannels?: string | null;
    selectedNotificationChannels?: NotificationDestination[];
    isDisabled: boolean;
    showMoreInfo: boolean;
    setShowMoreInfo: (show: boolean) => void;
    getNotificationDestinationsState: (form?: any) => { slack: boolean; email: boolean };
    connectedCapabilities: IntegrationCapability[];
    appPlatform?: AppPlatform;
    commsProviders: string;
    checkboxActions?: {
        slackChecked: boolean;
        emailChecked: boolean;
        handleChannelCheckboxChange: (channel: NotificationDestination, checked: boolean) => void;
        slackHeaderAction: React.ReactNode;
    };
}

const notificationSettingFormFieldsForNotification = ({
    notification,
    provider,
    hasNotificationsIntegration,
    userGroups,
    mentionType,
    appUsers,
    primaryChannels,
    isDisabled,
    showMoreInfo,
    setShowMoreInfo,
    getNotificationDestinationsState,
    connectedCapabilities,
    appPlatform,
    commsProviders,
    checkboxActions,
}: NotificationFormFieldsProps): FormFieldData[] => {
    const hasProviderAndNotificationIntegration = provider && hasNotificationsIntegration;
    const availableChannels = provider?.data?.availableChannels || [];
    const selectedMentionType =
        notification.mentionUsers?.length ? MentionType.MentionUsers : MentionType.MentionGroups;
    const isMentionedUsersType = mentionType === MentionType.MentionUsers;

    const showHourOfDaySetting =
        notification.id == AppNotificationType.ApprovalItemsReminder ||
        notification.id == AppNotificationType.RegressionItemsReminder;

    const showExpirationWarningDayPeriodSetting =
        notification.id == AppNotificationType.NoteExpiringAppleProfilesCertificates;

    const showMonitoringConditionsSetting = notification.id == AppNotificationType.HealthMetricsWarning;

    const showBundleSizeSettings = notification.id == AppNotificationType.BundleSizeIncrease;

    const showIncludeNewIssuesSetting = notification.id == AppNotificationType.AppStorePhasedReleaseUpdate;
    const isSlackProvider = provider?.type === IntegrationProviderType.Slack;
    const notificationsChannelTitle =
        provider?.type === IntegrationProviderType.AzureActiveDirectory ? 'Teams notifications' : 'Slack notifications';
    const showThreadWithRelatedNotifications =
        isSlackProvider &&
        [
            AppNotificationType.AppStorePhasedReleaseUpdate,
            AppNotificationType.RegressionBuildUpdated,
            AppNotificationType.RegressionStatusUpdated,
            AppNotificationType.ChecklistItemComment,
        ].includes(notification.id as AppNotificationType);

    const threadWithRelatedNotificationsTooltip = (notificationId: AppNotificationType): string => {
        switch (notificationId) {
            case AppNotificationType.AppStorePhasedReleaseUpdate:
                return 'Runway will also send critical updates to the channel, including notifications for paused, failed, or completed releases.';
            case AppNotificationType.RegressionBuildUpdated:
                return 'Runway will also send notifications about failed regression builds to the channel.';
            case AppNotificationType.RegressionStatusUpdated:
                return 'Runway will also send notifications about failed regression tests to the channel.';
            default:
                return '';
        }
    };

    const mentionedGroupsField: FormFieldData = createDestinationDependentField(
        {
            label: 'User groups',
            name: MentionType.MentionGroups,
            initialValue: notification.mentionGroups || [],
            fieldType: FieldType.Custom,
            extraFormItemProps: { hidden: isMentionedUsersType },
            rules: [],
        },
        (isSlackEnabled) => (
            <Select
                disabled={isDisabled || !isSlackEnabled}
                mode="multiple"
                allowClear
                placeholder="Select a user group/groups"
                filterOption={filterOptionPrefixMatch}
            >
                {Object.keys(userGroups).map((groupId, index) => (
                    <Select.Option key={index} value={groupId}>
                        {displayTextForUserChecklistGroup(userGroups[groupId])}
                    </Select.Option>
                ))}
            </Select>
        ),
        isDisabled,
        getNotificationDestinationsState,
    );

    const mentionedUsersField: FormFieldData = createDestinationDependentField(
        {
            label: 'Users',
            name: MentionType.MentionUsers,
            initialValue: notification.mentionUsers || [],
            fieldType: FieldType.Custom,
            extraFormItemProps: { hidden: !isMentionedUsersType },
            rules: [],
        },
        (isSlackEnabled) => (
            <Select
                disabled={isDisabled || !isSlackEnabled}
                allowClear
                mode="multiple"
                maxTagCount={'responsive'}
                placeholder="Select a user/users"
            >
                {appUsers.map((user, index) => (
                    <Select.Option value={user.email} key={index}>
                        {displayUserNameLong(user)}
                    </Select.Option>
                ))}
            </Select>
        ),
        isDisabled,
        getNotificationDestinationsState,
    );
    const fields: FormFieldData[] = [
        {
            name: 'enabled',
            initialValue: notification.enabled,
            fieldType: FieldType.Switch,
            rules: [{ required: true }],
        },
    ];

    const hourOfDaySettingHelpMessage = (notificationId: AppNotificationType): string => {
        switch (notificationId) {
            case AppNotificationType.ApprovalItemsReminder:
                return 'Choose the hour at which the reminder will be sent. Approval item reminders start a few hours after kickoff, or a few hours before submission.';
            case AppNotificationType.RegressionItemsReminder:
                return 'Choose the hour at which the reminder will be sent. Regression item reminders start when the Regression step becomes active and are sent every day until all items are complete.';
            default:
                return '';
        }
    };

    if (showHourOfDaySetting) {
        const hourOfDayUTC =
            notification.hourOfDayForNotification ? dayjs.utc().hour(notification.hourOfDayForNotification) : null;
        const hourOfDay = hourOfDayUTC ? dayjs(hourOfDayUTC).local() : null;
        const hourOfDayField = {
            label: 'Reminder time',
            name: 'hourOfDay',
            fieldType: FieldType.Custom,
            initialValue: hourOfDay,
            helpMessage: hourOfDaySettingHelpMessage(notification.id as AppNotificationType),
            renderField: () => {
                return <TimePicker format="h:00 A" showNow={false} style={{ width: '100%' }} />;
            },
        };
        fields.push(hourOfDayField);
    }

    if (showExpirationWarningDayPeriodSetting) {
        const profileCertsExpirationWarningDay =
            notification.appleProfileAndCertificateReminderDaysBeforeExpiration ?
                notification.appleProfileAndCertificateReminderDaysBeforeExpiration
            :   14;
        const appleProfileAndCertificateReminderDaysBeforeExpiration = {
            label: 'Reminders should start',
            name: 'appleProfileAndCertificateReminderDaysBeforeExpiration',
            fieldType: FieldType.Custom,
            initialValue: profileCertsExpirationWarningDay,
            helpMessage: 'ahead of profile and certification expiration',
            renderField: () => {
                return (
                    <InputNumber
                        min={1}
                        max={365}
                        precision={0}
                        type="number"
                        addonAfter="days"
                        defaultValue={profileCertsExpirationWarningDay}
                    />
                );
            },
        };

        const profileAndCertificateSelectOptions = Object.values(ProfileOrCertificateType).map((type) => ({
            value: type,
            label: type,
        }));

        const profileAndCertificateSelectOption = {
            label: 'Profile and certificate types',
            name: 'selectedProfileAndCertificateTypes',
            initialValue: notification.selectedProfileAndCertificateTypes ?? [],
            fieldType: FieldType.Custom,
            helpMessage:
                'Runway will send upcoming expiry reminders for the selected profile and certificate types only. Leave blank to send reminders for all profile and certificate types.',
            renderField: () => {
                return <Select options={profileAndCertificateSelectOptions} mode="multiple" />;
            },
        };

        fields.push(profileAndCertificateSelectOption);
        fields.push(appleProfileAndCertificateReminderDaysBeforeExpiration);
    }

    if (showBundleSizeSettings) {
        const isApple = appPlatform === undefined || getIsApplePlatform(appPlatform);
        const initialConditions: BundleSizeIncreaseCondition[] =
            notification.bundleSizeIncreaseConditions?.length ? notification.bundleSizeIncreaseConditions : [];

        const bundleSizeTypeOptions = [
            { value: BundleSizeConditionType.Download, label: 'Download size' },
            {
                value: BundleSizeConditionType.Install,
                label: isApple ? 'Install size' : 'Install size - not available for Android',
                disabled: !isApple,
            },
        ];
        const bundleSizeConditionsField = {
            label: 'Notify when bundle size increases (compared to live release) by more than:',
            name: 'bundleSizeIncreaseConditions',
            fieldType: FieldType.Custom,
            initialValue: initialConditions,
            style: { paddingBottom: 10 },
            rules: [
                () => ({
                    validator: (_, value: BundleSizeIncreaseCondition[] | undefined) => {
                        const conditions = value || [];
                        const types = conditions.map((c) => c?.type).filter(Boolean);
                        const seen = new Set<string>();
                        for (const t of types) {
                            if (seen.has(t)) {
                                return Promise.reject(new Error('You may only have one condition per type'));
                            }
                            seen.add(t);
                        }
                        return Promise.resolve();
                    },
                }),
            ],
            renderField: () => {
                const BundleSizeConditionsList = () => {
                    const form = Form.useFormInstance();
                    return (
                        <Form.List name="bundleSizeIncreaseConditions">
                            {(listFields, { add, remove }) => (
                                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                                    {listFields.map(({ key, name, ...rest }) => (
                                        <Flex
                                            key={key}
                                            align="flex-start"
                                            gap={8}
                                            wrap="wrap"
                                            style={{
                                                width: '100%',
                                                backgroundColor: antGrey.grey2,
                                                borderRadius: 8,
                                                padding: '20px 30px',
                                            }}
                                        >
                                            <Form.Item
                                                {...rest}
                                                name={[name, 'type']}
                                                rules={[{ required: true }]}
                                                style={{ marginBottom: 0, minWidth: 140 }}
                                            >
                                                <Select placeholder="Type" options={bundleSizeTypeOptions} />
                                            </Form.Item>
                                            <Form.Item
                                                name={[name, 'value']}
                                                rules={[
                                                    { required: true, message: 'Value is required' },
                                                    {
                                                        validator: (_, val) => {
                                                            if (form?.getFieldValue('enabled') === false)
                                                                return Promise.resolve();
                                                            if (val == null || val === '') return Promise.resolve();
                                                            if (Number(val) <= 0) {
                                                                return Promise.reject(
                                                                    new Error('Value must be greater than 0'),
                                                                );
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                                style={{ marginBottom: 0, width: 100 }}
                                            >
                                                <InputNumber min={1} max={10000} precision={0} />
                                            </Form.Item>
                                            <Form.Item
                                                name={[name, 'unit']}
                                                rules={[{ required: true }]}
                                                style={{ marginBottom: 0, minWidth: 100 }}
                                            >
                                                <Select
                                                    placeholder="Unit"
                                                    options={[
                                                        { value: BundleSizeConditionUnit.MB, label: 'MB' },
                                                        { value: BundleSizeConditionUnit.Percent, label: '% change' },
                                                    ]}
                                                />
                                            </Form.Item>
                                            <Button
                                                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                danger
                                                type="link"
                                                onClick={() => remove(name)}
                                            />
                                        </Flex>
                                    ))}
                                    <Form.Item noStyle shouldUpdate>
                                        {() => (
                                            <Flex
                                                justify="flex-start"
                                                align="center"
                                                style={{
                                                    width: '100%',
                                                    minHeight: 22,
                                                    paddingLeft: 30,
                                                    ...(listFields.length === 0 ? { marginTop: 12 } : {}),
                                                }}
                                                gap={8}
                                            >
                                                <Button
                                                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                                    disabled={isApple ? listFields.length >= 2 : listFields.length >= 1}
                                                    onClick={() => {
                                                        const current =
                                                            form?.getFieldValue('bundleSizeIncreaseConditions') ?? [];
                                                        const usedTypes = new Set(
                                                            (current as BundleSizeIncreaseCondition[])
                                                                .map((c) => c?.type)
                                                                .filter(Boolean),
                                                        );
                                                        const enabledOptions = bundleSizeTypeOptions.filter(
                                                            (opt) => !opt.disabled,
                                                        );
                                                        const nextType =
                                                            enabledOptions.find((opt) => !usedTypes.has(opt.value))
                                                                ?.value ?? BundleSizeConditionType.Download;
                                                        add({
                                                            type: nextType,
                                                            unit: BundleSizeConditionUnit.MB,
                                                            value: 10,
                                                        });
                                                    }}
                                                >
                                                    {listFields.length === 0 ?
                                                        'Add condition'
                                                    :   'Add another condition'}
                                                </Button>
                                            </Flex>
                                        )}
                                    </Form.Item>
                                </Space>
                            )}
                        </Form.List>
                    );
                };
                return <BundleSizeConditionsList />;
            },
        };
        fields.push(bundleSizeConditionsField);

        if (isApple) {
            const hasInitialCellular =
                notification.bundleSizeCellularDownloadLimitThresholdMB != null &&
                Number(notification.bundleSizeCellularDownloadLimitThresholdMB) >= 1;
            const bundleSizeCellularLimitField = {
                label: "Notify when bundle download size approaches within threshold of Apple's\u00A0200\u00A0MB cellular limit:",
                name: 'bundleSizeCellularDownloadLimitThresholdMB',
                fieldType: FieldType.Custom,
                initialValue: notification.bundleSizeCellularDownloadLimitThresholdMB,
                style: { paddingBottom: 10 },
                renderField: () => {
                    const CellularLimitField = () => {
                        const form = Form.useFormInstance();
                        const [showCellularInput, setShowCellularInput] = React.useState(hasInitialCellular);
                        return (
                            <Space direction="vertical" style={{ width: '100%' }} size={16}>
                                {showCellularInput ?
                                    <Flex
                                        align="center"
                                        gap={8}
                                        wrap="wrap"
                                        style={{
                                            width: '100%',
                                            backgroundColor: antGrey.grey2,
                                            borderRadius: 8,
                                            padding: '20px 30px',
                                        }}
                                    >
                                        <Form.Item
                                            name="bundleSizeCellularDownloadLimitThresholdMB"
                                            style={{ marginBottom: 0 }}
                                            rules={[{ required: true, message: 'Value is required' }]}
                                        >
                                            <InputNumber
                                                min={1}
                                                max={199}
                                                precision={0}
                                                style={{ minWidth: 120 }}
                                                addonAfter="MB"
                                            />
                                        </Form.Item>
                                        <Button
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            danger
                                            type="link"
                                            onClick={() => {
                                                form?.setFieldValue(
                                                    'bundleSizeCellularDownloadLimitThresholdMB',
                                                    undefined,
                                                );
                                                setShowCellularInput(false);
                                            }}
                                        />
                                    </Flex>
                                :   <Flex
                                        justify="flex-start"
                                        align="center"
                                        style={{
                                            width: '100%',
                                            minHeight: 22,
                                            paddingLeft: 30,
                                            marginTop: 12,
                                        }}
                                        gap={8}
                                    >
                                        <Button
                                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                            onClick={() => setShowCellularInput(true)}
                                        >
                                            Add condition
                                        </Button>
                                    </Flex>
                                }
                            </Space>
                        );
                    };
                    return <CellularLimitField />;
                },
            };
            fields.push(bundleSizeCellularLimitField);
        }

        const bundleSizeAlertNotifyForOptions = [
            { value: BundleSizeAlertNotifyFor.FirstBuildOnly, label: 'First build that exceeds threshold only' },
            { value: BundleSizeAlertNotifyFor.AllBuilds, label: 'Every build that exceeds threshold' },
        ];
        const bundleSizeAlertNotifyForField = {
            label: 'For a given release, send notification for:',
            name: 'bundleSizeAlertNotifyFor',
            fieldType: FieldType.Custom,
            initialValue: notification.bundleSizeAlertNotifyFor ?? BundleSizeAlertNotifyFor.FirstBuildOnly,
            style: { paddingBottom: 18 },
            renderField: () => <Select options={bundleSizeAlertNotifyForOptions} style={{ minWidth: 280 }} />,
        };
        fields.push(bundleSizeAlertNotifyForField);
    }

    if (notification.allowEmails) {
        fields.push(
            {
                name: 'notificationChannels',
                initialValue: notification.notificationChannels || [NotificationDestination.Slack],
                fieldType: FieldType.Custom,
                rules: [{ required: true }],
                style: { marginTop: 0, marginBottom: 0 },
                extraFormItemProps: { hidden: true },
                renderField: () => <span aria-hidden />,
            },
            ...(checkboxActions?.slackChecked === false && checkboxActions?.emailChecked === false ?
                [createErrorField('Please select at least one notification channel')]
            :   []),
        );
    } else {
        fields.push({
            name: 'notificationChannels',
            initialValue: [NotificationDestination.Slack],
            fieldType: FieldType.Custom,
            renderField: () => null,
            skipFormItemWrapper: true,
        });
    }

    if (showIncludeNewIssuesSetting) {
        const includeNewIssuesField = {
            label: '',
            name: 'includeNewIssuesIdentifiedByRunway',
            fieldType: FieldType.Custom,
            initialValue: notification.includeNewIssuesIdentifiedByRunway ?? false,
            extraFormItemProps: {
                valuePropName: 'checked',
                dependencies: ['notificationChannels'],
            },
            renderField: (_, __, ___, ____) => {
                return (
                    <Flex>
                        <Checkbox
                            defaultChecked={notification.includeNewIssuesIdentifiedByRunway ?? false}
                            disabled={isDisabled}
                        >
                            Include new stability issues
                        </Checkbox>
                        <TooltipInfoIcon tooltipContent="This will append information to this notification about any new stability issues identified by your stability monitoring integration (if connected)" />
                    </Flex>
                );
            },
        };
        fields.push(includeNewIssuesField);
    }

    if (showThreadWithRelatedNotifications) {
        const tooltipContent = threadWithRelatedNotificationsTooltip(notification.id as AppNotificationType);
        const threadWithRelatedNotificationsField = {
            label: '',
            name: 'threadWithRelatedNotifications',
            fieldType: FieldType.Custom,
            initialValue: notification.threadWithRelatedNotifications ?? false,
            extraFormItemProps: {
                valuePropName: 'checked',
                dependencies: ['notificationChannels'],
            },
            renderField: (_, __, ___, form) => {
                const { slack: isSlackEnabled } = getNotificationDestinationsState(form);

                return (
                    <Flex>
                        <Checkbox
                            defaultChecked={notification.threadWithRelatedNotifications ?? false}
                            disabled={isDisabled || !isSlackEnabled}
                        >
                            Thread related notifications on Slack
                        </Checkbox>
                        {tooltipContent && <TooltipInfoIcon tooltipContent={tooltipContent} />}
                    </Flex>
                );
            },
        };
        fields.push(threadWithRelatedNotificationsField);
    }

    const isSubmitNotification = [
        AppNotificationType.ManualSubmitToAppStore,
        AppNotificationType.AutoSubmitToAppStore,
    ].includes(notification.id as AppNotificationType);
    const hasPMIntegration = connectedCapabilities.includes(IntegrationCapability.FeatureManagement);

    if (isSubmitNotification) {
        const includeTicketListField: FormFieldData = {
            label: '',
            name: 'includeTicketListInSubmitNotifications',
            fieldType: FieldType.Custom,
            initialValue: notification.includeTicketListInSubmitNotifications ?? false,
            extraFormItemProps: {
                valuePropName: 'checked',
            },
            renderField: () => {
                return (
                    <Flex>
                        <Checkbox
                            defaultChecked={notification.includeTicketListInSubmitNotifications ?? false}
                            disabled={isDisabled || !hasPMIntegration}
                        >
                            Include a list of all tickets in the submission within this notification
                        </Checkbox>
                        <TooltipInfoIcon tooltipContent="This setting requires that Runway is integrated with your Project Management tool in integration settings" />
                    </Flex>
                );
            },
        };
        fields.push(includeTicketListField);
    }

    const isCherryPickNotification = notification.id === AppNotificationType.CherryPickFixRequest;

    if (isCherryPickNotification) {
        const includePROwnerField: FormFieldData = {
            label: '',
            name: 'includePROwnerInCherryPickNotifications',
            fieldType: FieldType.Custom,
            initialValue: notification.includePROwnerInCherryPickNotifications ?? false,
            extraFormItemProps: {
                valuePropName: 'checked',
            },
            renderField: () => {
                return (
                    <Checkbox
                        defaultChecked={notification.includePROwnerInCherryPickNotifications ?? false}
                        disabled={isDisabled}
                    >
                        Include PR owner in Slack notifications
                    </Checkbox>
                );
            },
        };
        fields.push(includePROwnerField);
    }

    const showPRCommitInfoField = [AppNotificationType.BuildFailed, AppNotificationType.BuildStarted].includes(
        notification.id as AppNotificationType,
    );

    if (showPRCommitInfoField) {
        const includePRCommitInfoField: FormFieldData = {
            label: '',
            name: 'includePRCommitInfo',
            fieldType: FieldType.Custom,
            initialValue: notification?.includePRCommitInfo ?? true,
            extraFormItemProps: {
                valuePropName: 'checked',
            },
            renderField: () => {
                return (
                    <Checkbox defaultChecked={notification?.includePRCommitInfo ?? true} disabled={isDisabled}>
                        Include PR/commit info on this notification
                    </Checkbox>
                );
            },
        };
        fields.push(includePRCommitInfoField);
    }

    if (showMonitoringConditionsSetting) {
        const monitoringConditionOptions: MonitoringConditionOption[] = [
            MonitoringAutomationConditionType.Adoption,
            MonitoringAutomationConditionType.PhasedReleaseDay,
        ].map((type) => ({
            id: type,
            title: displayTextForAutomationConditionType(type),
        }));

        const initialValues = [];
        if (notification.adoptionThreshold) {
            initialValues.push({
                id: MonitoringAutomationConditionType.Adoption,
                threshold: notification.adoptionThreshold,
            });
        }

        if (notification.phasedReleaseDayThreshold) {
            initialValues.push({
                id: MonitoringAutomationConditionType.PhasedReleaseDay,
                threshold: notification.phasedReleaseDayThreshold,
            });
        }

        fields.push(
            generateThresholdConditionsField(
                'thresholdConditions',
                'Send this notification when...',
                initialValues,
                connectedCapabilities,
                monitoringConditionOptions,
                { ignoreThresholdIfPhasedReleaseIsMissing: true, marginBottom: 24 },
            ),
        );
    }

    const showDirectDownloadLinkSettings = notification.id === AppNotificationType.BuildAvailArtifacts;

    if (showDirectDownloadLinkSettings) {
        const directDownloadLinkField: FormFieldData = {
            label: '',
            name: 'includeDirectDownloadLink',
            fieldType: FieldType.Custom,
            initialValue: notification.includeDirectDownloadLink ?? false,
            // Outer Form.Item must not wrap the limit fields: nested names merge into includeDirectDownloadLink and
            // break the boolean when InputNumber updates (see DirectDownloadLinkLimitFields).
            skipFormItemWrapper: true,
            renderField: () => (
                <Space direction="vertical" style={{ width: '100%', marginTop: 4, marginBottom: 22 }} size={8}>
                    <Flex align="center" gap={8} wrap="nowrap">
                        <Form.Item name="includeDirectDownloadLink" valuePropName="checked" noStyle>
                            <Checkbox disabled={isDisabled}>Include direct download link(s) in notification</Checkbox>
                        </Form.Item>
                        <TooltipInfoIcon tooltipContent="When enabled, a direct download link for each available artifact will be included in the notification and recipients can use those links to download artifacts without first authenticating in Runway." />
                    </Flex>
                    <DirectDownloadLinkLimitFields notification={notification} isDisabled={isDisabled} />
                </Space>
            ),
        };
        fields.push(directDownloadLinkField);
    }

    const mentionTypeField = {
        label: 'Mention individual users or user groups',
        name: 'mentionType',
        fieldType: FieldType.Custom,
        initialValue: selectedMentionType,
        extraFormItemProps: {
            dependencies: ['notificationChannels'],
        },
        renderField: (data, value, onChange, form) => {
            const { slack: isSlackEnabled } = getNotificationDestinationsState(form);

            return (
                <Radio.Group value={value} onChange={onChange} disabled={isDisabled || !isSlackEnabled}>
                    <Radio value={MentionType.MentionGroups}>User group</Radio>
                    <Radio value={MentionType.MentionUsers}>User</Radio>
                </Radio.Group>
            );
        },
    };

    let helpMessage: React.ReactNode;

    if (hasProviderAndNotificationIntegration) {
        helpMessage = (
            <Text type="secondary">
                Select or enter one or more channel names for this type of notification.
                <AntLink onClick={() => setShowMoreInfo(!showMoreInfo)}>
                    {showMoreInfo ? ' Less info' : ' More info'}
                </AntLink>
                {showMoreInfo && (
                    <Space direction="vertical" size={'small'} style={{ marginTop: '8px' }}>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>
                                <Text type="secondary">
                                    Pattern token strings are supported{' '}
                                    <Popover
                                        content={
                                            <PatternTokenHelperTextComponent
                                                availableTokens={CommsChannelAvailableTokens}
                                            />
                                        }
                                    >
                                        <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                    </Popover>{' '}
                                    <Tooltip title="If Runway cannot resolve selected tokens (e.g., no release name is set), the channel will not be created. Changing the release name or version during a release may disrupt notification delivery.">
                                        <WarningFilled style={{ color: runwayColors.statusYellow, paddingLeft: 6 }} />
                                    </Tooltip>
                                </Text>
                            </li>
                            <li>
                                <Text type="secondary">
                                    If the &lsquo;Create release-specific {commsProviders} channel(s)&rsquo; automation
                                    is enabled and a tokenized channel name is configured, Runway will automatically
                                    create a new {commsProviders} channel(s) for releases according to the entered
                                    pattern.
                                </Text>
                            </li>
                            <li>
                                <Text type="secondary">
                                    For private channels, you must first invite the Runway bot for Slack to the channel
                                    with the @Runway command, then enter the channel ID (not name) as free text here.
                                </Text>
                            </li>
                        </ul>
                    </Space>
                )}
            </Text>
        );
    } else {
        helpMessage = <Text type="secondary">Notifications integration not installed.</Text>;
    }

    const channelsHelpMessage = (
        <>
            {helpMessage}
            {primaryChannels && (
                <div style={{ paddingTop: 12 }}>
                    <Text type="secondary">Primary channel(s): {primaryChannels}</Text>
                </div>
            )}
        </>
    );

    const channelsField = {
        label: 'Channel(s) (overrides primary)',
        name: 'channels',
        helpMessage: channelsHelpMessage,
        initialValue: notification.channels.map((chan) => chan.id),
        fieldType: FieldType.Custom,
        extraFormItemProps: {
            dependencies: ['notificationChannels'],
        },
        rules: [
            ({ getFieldValue }) => ({
                validator: (_, values) => {
                    const currentChannels = getFieldValue('notificationChannels') || [];
                    const isSlackEnabled = hasProviderAndNotificationIntegration && currentChannels.includes('SLACK');
                    return isSlackEnabled ? commsChannelsValidatorFunction(provider.type, values) : Promise.resolve();
                },
            }),
        ],
        data: availableChannels,
        renderField: (data, _, __, form) => {
            const { slack: isSlackEnabled } = getNotificationDestinationsState(form);

            return (
                <Select
                    placeholder={'Select'}
                    mode="tags"
                    notFoundContent={null}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    disabled={isDisabled || !hasProviderAndNotificationIntegration || !isSlackEnabled}
                >
                    {data.map((channel, index) => {
                        return (
                            <Select.Option value={channel.id} key={index} label={channel.name}>
                                {channel.name}
                            </Select.Option>
                        );
                    })}
                </Select>
            );
        },
    };

    const customCopyField: FormFieldData = {
        label: 'Append custom text',
        name: 'customCopy',
        initialValue: notification.customCopy ?? '',
        fieldType: FieldType.Custom,
        helpMessage: (
            <Text type="secondary">
                Pattern token strings are supported.{' '}
                <Popover
                    content={
                        <div style={{ overflow: 'scroll', maxWidth: '40vw', maxHeight: '40vh' }}>
                            <PatternTokenHelperTextComponent availableTokens={AllPatternTokens} />
                        </div>
                    }
                    placement="top"
                >
                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                </Popover>
            </Text>
        ),
        extraFormItemProps: { validateTrigger: 'onBlur', dependencies: ['notificationChannels'] },
        rules: [
            {
                validator: async (_, value) => {
                    if (!value) return;

                    const tokensInText = [...value.matchAll(/\{([^\s{}]+)\}/g)].map((match) => match[1]);
                    const invalidTokens = tokensInText.filter(
                        (t: string) => !AllPatternTokens.includes(`{${t}}` as PatternToken),
                    );

                    if (invalidTokens.length) {
                        const label = invalidTokens.length > 1 ? 'patterns' : 'pattern';
                        const errorMsg = `Invalid ${label}: ${invalidTokens.map((t) => `{${t}}`).join(', ')}`;
                        throw new Error(errorMsg);
                    }
                },
            },
            {
                warningOnly: true,
                validator: async (_, value) => {
                    if (!value) return;
                    let depth = 0;
                    let hasIssue = false;

                    for (const char of value) {
                        if (char === '{') depth++;
                        if (char === '}') depth--;

                        if (depth > 1 || depth < 0) {
                            hasIssue = true;
                            break;
                        }
                    }
                    if (hasIssue || depth !== 0) {
                        throw new Error(
                            "You may have an invalid pattern token: check your braces for an unclosed or nested '{' or '}'",
                        );
                    }
                },
            },
        ],
        renderField: (data, _, __, form) => {
            const { slack: isSlackEnabled } = getNotificationDestinationsState(form);
            return (
                <TextArea
                    placeholder={'Enter custom text here'}
                    style={{ resize: 'none' }}
                    rows={3}
                    disabled={isDisabled || !hasProviderAndNotificationIntegration || !isSlackEnabled}
                />
            );
        },
    };

    fields.push({
        fieldType: FieldType.WrappedGroup,
        groupedFields: [mentionTypeField, mentionedGroupsField, mentionedUsersField, channelsField, customCopyField],
        groupChildrenWrapper: (children) => {
            if (notification.allowEmails) {
                return (
                    <NotificationChannelSection
                        title={notificationsChannelTitle}
                        isChecked={checkboxActions?.slackChecked ?? true}
                        onCheckboxChange={(checked) =>
                            checkboxActions?.handleChannelCheckboxChange(NotificationDestination.Slack, checked)
                        }
                        disabled={isDisabled}
                        headerAction={checkboxActions?.slackHeaderAction}
                    >
                        {children}
                    </NotificationChannelSection>
                );
            } else {
                return (
                    <NotificationSectionWrapper
                        title={notificationsChannelTitle}
                        headerAction={checkboxActions?.slackHeaderAction}
                    >
                        {children}
                    </NotificationSectionWrapper>
                );
            }
        },
    });

    if (notification.allowEmails) {
        const emailField = {
            label: 'Emails',
            name: 'emailsToInclude',
            helpMessage: 'Enter one or more email addresses to receive this type of notification via email.',
            initialValue: notification.emailsToInclude || [],
            fieldType: FieldType.Custom,
            extraFormItemProps: {
                dependencies: ['notificationChannels'],
            },
            rules: emailValidationRules([], false),
            data: notification.emailsToInclude || [],
            renderField: (data, _, __, form) => {
                const { email: isEmailEnabled } = getNotificationDestinationsState(form);

                return (
                    <Select
                        placeholder="Enter email addresses"
                        mode="tags"
                        notFoundContent={null}
                        showSearch
                        allowClear
                        tokenSeparators={[',']}
                        disabled={isDisabled || !isEmailEnabled}
                    >
                        {data.map((email, index) => {
                            return (
                                <Select.Option value={email} key={index}>
                                    {email}
                                </Select.Option>
                            );
                        })}
                    </Select>
                );
            },
        };

        fields.push({
            fieldType: FieldType.WrappedGroup,
            groupedFields: [emailField],
            groupChildrenWrapper: (children) => (
                <NotificationChannelSection
                    title="Email notifications"
                    isChecked={checkboxActions?.emailChecked ?? false}
                    onCheckboxChange={(checked) =>
                        checkboxActions?.handleChannelCheckboxChange(NotificationDestination.Email, checked)
                    }
                    disabled={isDisabled}
                >
                    {children}
                </NotificationChannelSection>
            ),
        });
    }

    return fields;
};
